import React, { useEffect, useRef, useState } from 'react'

import { ReactComponent as DownButtonSvg } from 'assets/down_button.svg'
import { ReactComponent as UpButtonSvg } from 'assets/up_button.svg'
import { Button } from '@mui/material'
import { Bet } from 'hooks/useDetailedBetResult'
import CountDownWrapper from 'assets/countdown_wrapper.png'
import { useAppContext } from 'context/AppContext'

interface BetPanelProps {
  isInactive: boolean
  onClick: (bet: Bet) => void
  countdown: number
}

const BetPanel: React.FC<BetPanelProps> = ({
  isInactive,
  onClick,
  countdown,
}) => {
  const { betCount, isRefueling } = useAppContext()
  const [activeButtons, setActiveButtons] = useState({ up: false, down: false })
  const [isAnimating, setIsAnimating] = useState(false)

  const prevCountdownRef = useRef(countdown)

  const handleTouchStart = (bet: string) => () => {
    setActiveButtons((prevState) => ({ ...prevState, [bet]: true }))
  }

  const handleTouchEnd = (bet: string) => () => {
    setTimeout(() => {
      setActiveButtons((prevState) => ({ ...prevState, [bet]: false }))
    }, 200)
  }

  useEffect(() => {
    if (countdown !== prevCountdownRef.current) {
      setIsAnimating(true)

      const timeout = setTimeout(() => {
        prevCountdownRef.current = countdown
        setIsAnimating(false)
      }, 500)

      return () => clearTimeout(timeout)
    }
  }, [countdown, prevCountdownRef])

  return (
    <div className="Race__bet-panel">
      <div className="Race__controls">
        <Button
          onClick={() => onClick(Bet.DOWN)}
          disabled={isInactive}
          onTouchStart={handleTouchStart('down')}
          onTouchEnd={handleTouchEnd('down')}
          sx={{
            position: 'relative',
            height: 169,
            background: 'transparent',
            border: 'none',
            padding: 0,
            width: 119,
            zIndex: 10,
            '& svg': {
              transition: 'transform 0.2s ease, fill 0.2s ease',
              transform: activeButtons.down ? 'scale(1.1)' : 'scale(1)',
            },
          }}
        >
          <DownButtonSvg
            style={{
              width: '100%',
              height: '100%',
              transition: 'transform 0.2s ease, fill 0.2s ease',
            }}
          />
        </Button>

        <div className="Race__countdown-wrapper">
          <div className="Race__countdown">
            <span
              className={`Race__seconds-value ${isAnimating ? 'animate' : ''}`}
            >
              {prevCountdownRef.current}
            </span>{' '}
            <span className="Race__seconds-text">Seconds</span>
          </div>

          <img
            className="Race__countdown-border"
            width={130}
            height={91}
            src={CountDownWrapper}
            alt="mask"
          />
        </div>

        <div className="Race__gas-tank-wrapper">
          <div className="Race__gas-tank">
            <div className="Race__gas-tank-text">Gas Tank</div>

            {isRefueling ? (
              'Refueling...'
            ) : (
              <div className="Race__gas-tank-items">
                {[...Array(10)].map((_, index) => {
                  const itemCount = index + 1
                  return (
                    <div
                      className={`Race__gas-tank-item ${itemCount > betCount ? 'Race__gas-tank-item--used' : 'Race__gas-tank-item--full'}`}
                      key={itemCount}
                    ></div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={() => onClick(Bet.UP)}
          disabled={isInactive}
          onTouchStart={handleTouchStart('up')}
          onTouchEnd={handleTouchEnd('up')}
          sx={{
            position: 'relative',
            height: 169,
            width: 119,
            background: 'transparent',
            border: 'none',
            padding: 0,
            zIndex: 10,
            '& svg': {
              transition: 'transform 0.2s ease, fill 0.2s ease',
              transform: activeButtons.up ? 'scale(1.1)' : 'scale(1)',
            },
          }}
        >
          <UpButtonSvg
            style={{
              width: '100%',
              height: '100%',
              transition: 'transform 0.2s ease, fill 0.2s ease',
            }}
          />
        </Button>
      </div>
    </div>
  )
}

export default React.memo(BetPanel)
